import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import FlexTable from "../../../components/ui-components/FlexTable";
import StatusPill from "../../Budgets/components/StatusPill";
import { fmtEur, fmtEurSigned } from "./format";

// SEVERITY_TONE mirrors HorizonTableBlock/BudgetVarianceTableBlock's adverse/favorable/
// neutral colour vocabulary: a high-severity at-risk row is adverse (red), medium is
// neutral (the existing palette has no distinct "warning" ink), low is favorable (green).
const SEVERITY_TONE = { high: "a4-kpi-tone-adverse", medium: "a4-kpi-tone-neutral", low: "a4-kpi-tone-favorable" };

function capitalize(s) {
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fmtDays(n) {
  return n == null ? "—" : `${n} days`;
}

// derivePageStatus maps the at-risk rows' backend-computed severity onto StatusPill's
// vocabulary WITHOUT re-deriving any threshold: any "high" severity row -> "over"; else any
// "medium" -> "at_risk"; else (no at-risk rows, or only "low") -> "green".
function derivePageStatus(atRisk) {
  if (atRisk.some((row) => row.severity === "high")) return "over";
  if (atRisk.some((row) => row.severity === "medium")) return "at_risk";
  return "green";
}

const atRiskColumns = [
  { Header: "Budget", accessor: "name" },
  { Header: "Owner", accessor: "owner", Cell: ({ value }) => value || "—" },
  { Header: "Cap", accessor: "capCents", align: "right", Cell: ({ value }) => fmtEur(value / 100) },
  { Header: "Projected", accessor: "projectedCents", align: "right", Cell: ({ value }) => fmtEur(value / 100) },
  {
    Header: "Over by",
    accessor: "overByCents",
    align: "right",
    Cell: ({ value }) => <span className="a4-kpi-tone-adverse">{fmtEurSigned(value / 100)}</span>,
  },
  { Header: "Days to breach", accessor: "daysToBreach", align: "right", Cell: ({ value }) => fmtDays(value) },
  {
    Header: "Severity",
    accessor: "severity",
    align: "center",
    Cell: ({ value }) => <span className={SEVERITY_TONE[value]}>{capitalize(value)}</span>,
  },
];

// KpiTile - a single tile reusing the same a4-kpi-tile* classes KPIStripBlock's tiles
// already draw from BoardReportPrint.css, so this block's KPI row matches the app's
// existing print styling without introducing new CSS.
function KpiTile({ label, value, tone = "neutral" }) {
  return (
    <div className="a4-kpi-tile">
      <div className="a4-kpi-tile-label">{label}</div>
      <div className={`a4-kpi-tile-value a4-kpi-tone-${tone}`}>{value}</div>
    </div>
  );
}

KpiTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  tone: PropTypes.oneOf(["adverse", "favorable", "neutral"]),
};

// BreachTimeline - a simple horizontal-bar rendering of upcoming breach dates. `timeline` is
// assumed pre-sorted soonest-first by the backend (BuildBudgetRiskOverview); bar length is
// index-derived (soonest = longest) rather than date-math, so rendering stays deterministic
// regardless of "now".
function BreachTimeline({ timeline }) {
  if (timeline.length === 0) return null;
  const n = timeline.length;
  return (
    <div className="a4-breach-timeline" style={{ marginTop: "4mm", display: "flex", flexDirection: "column", gap: "1.5mm" }}>
      {timeline.map((item, index) => {
        const widthPct = Math.max(15, 100 - (index * (70 / Math.max(n, 1))));
        const color = item.severity === "high" ? "#a33" : item.severity === "medium" ? "#8fc1d6" : "#1f7a4d";
        return (
          <div key={item.budgetId} style={{ display: "flex", alignItems: "center", gap: "3mm", fontSize: "8.5pt" }}>
            <span style={{ minWidth: "30mm" }}>{item.name}</span>
            <span style={{ flex: 1, height: "3mm", background: "#eee", borderRadius: "1mm" }}>
              <span style={{ display: "block", width: `${widthPct}%`, height: "100%", background: color, borderRadius: "1mm" }} />
            </span>
            <span style={{ minWidth: "20mm", color: "#6b6b6b" }}>{item.breachDate}</span>
          </div>
        );
      })}
    </div>
  );
}

BreachTimeline.propTypes = {
  timeline: PropTypes.arrayOf(
    PropTypes.shape({
      budgetId: PropTypes.number,
      name: PropTypes.string,
      breachDate: PropTypes.string,
      severity: PropTypes.string,
    })
  ).isRequired,
};

/**
 * BudgetRiskOverviewBlock - the Risk Overview page's sole data block (budget_risk_overview
 * block type). Per the Step-1 decision, this block renders the KPI row + status pill itself
 * from the same figure rather than relying on the separate shared status_badge/kpi_strip
 * blocks (whose manifest entries were dropped for this page) - fewer moving parts, and
 * StatusBadgeBlock/KPIStripBlock stay untouched since monthly_summary still depends on them.
 *
 * GET /api/v1/board-reports/{boardReportId}/runs/{runId}/figures/budget-risk-overview ->
 * envelope key "budget_risk_overview" -> { kpis, atRisk: [BudgetRiskRow], timeline:
 * [BreachTimelineEntry] } (backend internal/boardfigures/budget_risk.go).
 */
const BudgetRiskOverviewBlock = ({ title = "Budget risk overview", boardReportId, runId }) => {
  const { data, isLoading, error } = useBlockData(
    `/api/v1/board-reports/${boardReportId}/runs/${runId}/figures/budget-risk-overview`
  );

  const kpis = data?.budget_risk_overview?.kpis ?? null;
  const atRisk = data?.budget_risk_overview?.atRisk ?? [];
  const timeline = data?.budget_risk_overview?.timeline ?? [];
  const pageStatus = derivePageStatus(atRisk);

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error} className="a4-budget-risk-overview">
      <div className="a4-risk-overview-header" style={{ marginBottom: "4mm" }}>
        <StatusPill status={pageStatus} />
      </div>

      {kpis && (
        <div className="a4-kpi-tiles">
          <KpiTile label="At risk" value={`${kpis.atRisk} / ${kpis.activeTotal}`} tone={kpis.atRisk > 0 ? "adverse" : "favorable"} />
          <KpiTile label="Overage exposure" value={fmtEur(kpis.overageExposureCents / 100)} tone={kpis.overageExposureCents > 0 ? "adverse" : "neutral"} />
          <KpiTile
            label="Nearest breach"
            value={kpis.nearestBreachDays != null ? `${fmtDays(kpis.nearestBreachDays)} · ${kpis.nearestBreachBudget}` : "—"}
          />
          <KpiTile
            label="Portfolio projected"
            value={`${fmtEur(kpis.portfolioProjectedCents / 100)} / ${fmtEur(kpis.portfolioCapCents / 100)}`}
          />
        </div>
      )}

      {atRisk.length > 0 ? (
        <FlexTable columns={atRiskColumns} data={atRisk} getRowId={(row) => row.budgetId} />
      ) : (
        // No at-risk budgets: render a calm note instead of a bare header row (FlexTable
        // always draws its header group even with zero data rows).
        <p className="a4-empty-note" style={{ color: "#6b6b6b", fontSize: "9pt", margin: "2mm 0" }}>
          No budgets are currently projected to breach their caps.
        </p>
      )}

      <BreachTimeline timeline={timeline} />
    </BaseBlock>
  );
};

BudgetRiskOverviewBlock.propTypes = {
  title: PropTypes.string,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default BudgetRiskOverviewBlock;
